import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { WORKSPACE_CONTENT_CONTAINER_ID } from 'Shared/constants/constants';
import { editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';
import { ItemType } from 'Shared/constants/types';
import get from 'lodash/get';

import { addEventListenerForScrolableElement, removeEventListenerForScrolableElement } from './DnDScrolling';

/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["item"] }] */

export const itemWithNestingCanDrag = forbidDrag => !forbidDrag;

export const itemWithNestingBeginDrag = ({ getState, uuid: ItemUuid, selector = 'workspace.meeting_items' }) => {
  addEventListenerForScrolableElement(WORKSPACE_CONTENT_CONTAINER_ID);

  const state = getState();
  const selectedUuids = state.selectedUuids;
  const items = get(state, selector);
  const clickedItem = items.find(({ uuid }) => uuid === ItemUuid);
  const selectedItems = items.filter(({ uuid }) => selectedUuids.includes(uuid));
  const movingItems = selectedItems.includes(clickedItem) ? selectedItems : [clickedItem];
  const dragUuid = movingItems.map(({ uuid }) => uuid);

  return {
    body: editorStateForEditorComponent(movingItems[0].body),
    dragUuid,
    hasHighPriorityItems: Boolean(movingItems.find(({ isHighPriority }) => isHighPriority)),
    hasChildrensOrIsSection: Boolean(
      movingItems.find(
        ({ uuid, isSection }) => Boolean(items.filter(({ parentUuid }) => parentUuid === uuid).length) || isSection
      )
    ),
  };
};

export const itemWithNestingEndDrag = (
  { onDrop, onNestByDrag, dispatch },
  monitor,
  { dragUuid, hoverOffset, isDropAsChild, isDropOnEmptyArea }
) => {
  removeEventListenerForScrolableElement();

  if (isDropOnEmptyArea && !isDropAsChild && onDrop) {
    onDrop({
      draggedItemUuids: dragUuid,
      hoverOffset,
    });
    dispatch(updateSelectedUuids(dragUuid[0], { isOneSelected: true }));
    return;
  }

  const dropResult = monitor.getDropResult();
  if (!dropResult) return;

  const { hoveredItemUuid, hoveredItemId } = dropResult;
  if (!hoveredItemUuid) return;

  if (isDropAsChild) {
    if (!onNestByDrag) return;
    onNestByDrag({
      draggedItemUuids: dragUuid,
      hoveredItemUuid,
      hoveredItemId,
    });
  } else {
    if (!onDrop) return;
    onDrop({
      draggedItemUuids: dragUuid,
      hoveredItemUuid,
      hoverOffset,
    });
  }

  if (dragUuid.length === 1) {
    dispatch(updateSelectedUuids(dragUuid[0], { isOneSelected: true }));
  }
};

export const itemWithNestingIsDragging = ({ uuid }, monitor) => {
  const { dragUuid } = monitor.getItem();
  return Boolean(dragUuid.includes(uuid));
};

export const itemWithNestingDrop = ({ uuid, id }, monitor) => {
  if (monitor.didDrop()) return undefined;
  return { hoveredItemUuid: uuid, hoveredItemId: id };
};

export const itemWithNestingHover = (
  { containerRef, parentUuid, itemType, isHighPriority, setHoverOffset, setIsDropAsChild, isLast },
  monitor,
  item
) => {
  if (!containerRef && !containerRef.current) return;
  const node = containerRef.current;
  if (!monitor.canDrop()) return;

  const { hasHighPriorityItems, hasChildrensOrIsSection } = item;
  const hoverBoundingRect = node.getBoundingClientRect();
  const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
  const clientOffset = monitor.getClientOffset();
  const hoverClientY = clientOffset.y - hoverBoundingRect.top;
  const hoverClientX = clientOffset.x - hoverBoundingRect.left;
  const hoverOffset = hoverClientY < hoverMiddleY ? 0 : 1;
  setHoverOffset(hoverOffset);

  // Hover conditions for action items
  if (itemType === ItemType.actionItem) {
    // Conditions for being able to drop on EmptyArea after ActionItems
    if (isLast && hoverOffset && !parentUuid) item.isDropOnEmptyArea = true;
    else if (item.isDropOnEmptyArea) item.isDropOnEmptyArea = false;

    const cursorPositionForDropAsChild = hoverClientX >= 80 && hoverClientY > hoverMiddleY;

    // Conditions for being able to drop as a child element
    if (
      !hasChildrensOrIsSection &&
      hasHighPriorityItems === isHighPriority &&
      !parentUuid &&
      cursorPositionForDropAsChild &&
      monitor.isOver({ shallow: true })
    ) {
      // Component variable for displaying DropArea as a child element
      setIsDropAsChild(true);
      // DnD variable for adding an element as a child in the endDrag function()
      item.isDropAsChild = true;
    } else {
      setIsDropAsChild(false);
      item.isDropAsChild = false;
    }

    // Hover conditions for discussion topics
  } else {
    const cursorPositionForDropAsChild =
      clientOffset.y < hoverBoundingRect.bottom - 10 && clientOffset.y > hoverBoundingRect.top + 10;

    // Conditions for being able to drop as a child element
    if (!hasChildrensOrIsSection && !parentUuid && cursorPositionForDropAsChild && monitor.isOver({ shallow: true })) {
      setIsDropAsChild(true);
      item.isDropAsChild = true;
    } else {
      setIsDropAsChild(false);
      item.isDropAsChild = false;
    }
  }

  item.hoverOffset = hoverOffset;
};

export const itemWithNestingCanDrop = (
  { parentUuid, itemType, isHighPriority, uuid },
  { dragUuid, hasHighPriorityItems, hasChildrensOrIsSection }
) => {
  if (
    (hasChildrensOrIsSection && parentUuid) ||
    dragUuid.includes(uuid) ||
    (itemType === ItemType.actionItem && hasHighPriorityItems !== isHighPriority)
  ) {
    return false;
  }
  return true;
};
